import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Inventory as InventoryIcon,
  History as HistoryIcon,
  ShoppingCart as ShopIcon,
} from '@mui/icons-material';
import { 
  getEnrichedSealedProducts, 
  updateSealedProductOwnership, 
  EnrichedSealedProduct 
} from '../../services/opSealedService';

const SealedProductTracker: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<EnrichedSealedProduct[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingProduct, setEditingProduct] = useState<EnrichedSealedProduct | null>(null);
  const [editQuantity, setEditQuantity] = useState(0);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const data = await getEnrichedSealedProducts();
      setProducts(data);
    } catch (error) {
      console.error('Failed to fetch sealed products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = (product: EnrichedSealedProduct) => {
    setEditingProduct(product);
    setEditQuantity(product.quantity || 0);
  };

  const handleSaveOwnership = async () => {
    if (!editingProduct) return;
    
    try {
      await updateSealedProductOwnership(
        editingProduct.id,
        editQuantity,
        editingProduct.setId
      );
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      console.error('Failed to update ownership:', error);
    }
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.setId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 6 }}>
        <Typography variant="h3" fontWeight="800" gutterBottom sx={{ 
          background: 'linear-gradient(45deg, #FFD700 30%, #FFA500 90%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 1
        }}>
          One Piece Sealed Tracker
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 4, fontWeight: 400 }}>
          Manage your booster boxes, starter decks, and special collections.
        </Typography>

        <Paper 
          elevation={0}
          sx={{ 
            p: 1, 
            display: 'flex', 
            alignItems: 'center', 
            borderRadius: 4,
            bgcolor: alpha(theme.palette.background.paper, 0.4),
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            maxWidth: 600
          }}
        >
          <TextField
            fullWidth
            placeholder="Search products, sets, or types..."
            variant="standard"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start" sx={{ ml: 2, mr: 1 }}>
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
              sx: { py: 1, px: 1 }
            }}
          />
        </Paper>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 20 }}>
          <CircularProgress size={60} thickness={4} />
        </Box>
      ) : (
        <Grid container spacing={4}>
          {filteredProducts.map((product) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: 4,
                overflow: 'hidden',
                transition: 'all 0.3s ease-in-out',
                bgcolor: alpha(theme.palette.background.paper, 0.6),
                border: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.2)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }
              }}>
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="240"
                    image={product.imageUrl}
                    alt={product.name}
                    sx={{ objectFit: 'cover' }}
                  />
                  <Chip
                    label={product.type}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      backdropFilter: 'blur(8px)',
                      background: alpha(theme.palette.background.paper, 0.8),
                      fontWeight: 600
                    }}
                  />
                  {product.owned && (
                    <Chip
                      label={`${product.quantity} Owned`}
                      color="success"
                      size="small"
                      sx={{
                        position: 'absolute',
                        bottom: 16,
                        left: 16,
                        fontWeight: 700
                      }}
                    />
                  )}
                </Box>
                <CardContent sx={{ flexGrow: 1, pt: 3 }}>
                  <Typography variant="overline" color="primary" fontWeight="700">
                    {product.setId}
                  </Typography>
                  <Typography variant="h6" fontWeight="700" sx={{ mb: 1, lineHeight: 1.2 }}>
                    {product.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Release: {new Date(product.releaseDate).toLocaleDateString()}
                  </Typography>
                  <Typography variant="h6" color="secondary" sx={{ mt: 1, fontWeight: 700 }}>
                    ${product.msrp?.toFixed(2)} <Typography component="span" variant="caption" color="text.secondary">MSRP</Typography>
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button 
                    fullWidth 
                    variant={product.owned ? "outlined" : "contained"}
                    startIcon={product.owned ? <HistoryIcon /> : <AddIcon />}
                    onClick={() => handleOpenEdit(product)}
                    sx={{ 
                      borderRadius: 2, 
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    {product.owned ? 'Manage Stock' : 'Add to Collection'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Edit Dialog */}
      <Dialog 
        open={!!editingProduct} 
        onClose={() => setEditingProduct(null)}
        PaperProps={{
          sx: { borderRadius: 4, width: '100%', maxWidth: 400 }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 4 }}>
          <Typography variant="h5" fontWeight="800">
            {editingProduct?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Update your sealed inventory
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <IconButton 
              onClick={() => setEditQuantity(Math.max(0, editQuantity - 1))}
              disabled={editQuantity === 0}
              sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
            >
              <RemoveIcon />
            </IconButton>
            <Typography variant="h2" sx={{ mx: 4, fontWeight: 800, minWidth: 60, textAlign: 'center' }}>
              {editQuantity}
            </Typography>
            <IconButton 
              onClick={() => setEditQuantity(editQuantity + 1)}
              sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
            >
              <AddIcon />
            </IconButton>
          </Box>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Total Value: ${( (editingProduct?.msrp || 0) * editQuantity).toFixed(2)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 4, pt: 0 }}>
          <Button 
            fullWidth 
            variant="contained" 
            size="large"
            onClick={handleSaveOwnership}
            sx={{ borderRadius: 2, py: 1.5, fontWeight: 700 }}
          >
            Save Inventory
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SealedProductTracker;
